import { format } from "date-fns";

import { es } from "date-fns/locale";

import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import TimeSeriesChartCard from "../../../shared/components/charts/TimeSeriesChartCard";

import LoadingOverlay from "../../../shared/components/LoadingOverlay";

import PageHeader from "../../../shared/components/PageHeader";

import { AppView } from "./AppView";

import { FunnelGuestView } from "./FunnelGuestView";

import { FunnelView } from "./FunnelView";

import type {
  AnalyticsGranularity,
  AnalyticsRange,
} from "../../stats/api/statsApi";

import {
  useAnalyticsOverviewQuery,
  useBreakdownQuery,
  useTopPagesQuery,
  useTopReferrersQuery,
} from "../../stats/hooks/useStats";

import {
  BreakdownTable,
  StatsCard,
  TopPagesTable,
  TopReferrersTable,
} from "../../stats/components/subcomponents";

const analyticsPalette = {
  border: "rgb(34, 197, 94)",

  background: "rgba(34, 197, 94, 0.18)",

  point: "rgb(74, 222, 128)",
} as const;

const RANGE_OPTIONS: AnalyticsRange[] = ["live", "1d", "7d", "28d", "90d"];

const granularityForRange = (range: AnalyticsRange): AnalyticsGranularity => {
  if (range === "live" || range === "1d") return "hour";

  if (range === "7d") return "day";

  if (range === "28d") return "week";

  return "month";
};

const formatPeriodLabel = (iso: string, granularity: AnalyticsGranularity) => {
  const date = new Date(iso);

  if (granularity === "hour") {
    return format(date, "HH:mm", { locale: es });
  }

  if (granularity === "week") {
    const end = new Date(date);

    end.setUTCDate(end.getUTCDate() + 6);

    return `${format(date, "dd/MM", { locale: es })} - ${format(end, "dd/MM", { locale: es })}`;
  }

  if (granularity === "month") {
    return format(date, "MMM yy", { locale: es });
  }

  return format(date, "dd/MM/yy", { locale: es });
};

const formatDurationMmSs = (seconds: number) => {
  const total = Math.max(0, Math.round(seconds));

  const minutes = Math.floor(total / 60);

  const remainder = total % 60;

  return `${minutes.toString().padStart(2, "0")}:${remainder.toString().padStart(2, "0")}`;
};

const AnalyticsPage = () => {
  const { t } = useTranslation("common");

  const [range, setRange] = useState<AnalyticsRange>("7d");

  const granularity = granularityForRange(range);

  const overviewQuery = useAnalyticsOverviewQuery(range, granularity);

  const topPagesQuery = useTopPagesQuery(range, 10);

  const topReferrersQuery = useTopReferrersQuery(range, 10);

  const countriesQuery = useBreakdownQuery(range, "country", 10);

  const browsersQuery = useBreakdownQuery(range, "browser", 10);

  const osQuery = useBreakdownQuery(range, "os", 10);

  const devicesQuery = useBreakdownQuery(range, "device", 10);

  const languagesQuery = useBreakdownQuery(range, "language", 10);

  const cards = useMemo(() => {
    const overview = overviewQuery.data;

    if (!overview) return [];

    return [
      { title: t("ANALYTICS.activeUsers"), value: overview.cards.activeUsers },

      { title: t("ANALYTICS.sessions"), value: overview.cards.sessions },

      { title: t("ANALYTICS.pageViews"), value: overview.cards.pageViews },

      {
        title: t("ANALYTICS.eventsPerSession"),

        value: Number(overview.cards.eventsPerSession.toFixed(2)),
      },

      {
        title: t("ANALYTICS.avgDuration"),

        value: overview.cards.avgSessionDurationSec,

        displayValue: formatDurationMmSs(overview.cards.avgSessionDurationSec),
      },

      {
        title: t("ANALYTICS.bounceRate"),

        value: overview.cards.bounceRate,

        displayValue: `${(overview.cards.bounceRate * 100).toFixed(1)}%`,
      },
    ];
  }, [overviewQuery.data, t]);

  const chartPoints = useMemo(() => {
    const overview = overviewQuery.data;

    if (!overview) return [];

    return overview.timeseries.map((point) => ({
      label: formatPeriodLabel(point.period, granularity),

      value: Math.round(point.count),
    }));
  }, [overviewQuery.data, granularity]);

  const isBusy =
    overviewQuery.isLoading ||
    overviewQuery.isFetching ||
    topPagesQuery.isFetching ||
    topReferrersQuery.isFetching ||
    countriesQuery.isFetching ||
    browsersQuery.isFetching ||
    osQuery.isFetching ||
    devicesQuery.isFetching ||
    languagesQuery.isFetching;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("ANALYTICS.title")}
        subtitle={t("ANALYTICS.subtitle")}
      />

      <div className="flex flex-wrap items-center gap-2">
        {RANGE_OPTIONS.map((opt) => {
          const isActive = range === opt;

          const label =
            opt === "live"
              ? t("ANALYTICS.rangeLive")
              : opt === "1d"
                ? t("ANALYTICS.rangeToday")
                : opt;

          return (
            <Button
              key={opt}
              size="sm"
              variant={isActive ? "default" : "outline"}
              onClick={() => setRange(opt)}
              className="gap-1.5"
            >
              {opt === "live" ? (
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                </span>
              ) : null}
              {label}
            </Button>
          );
        })}
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">
            {t("ANALYTICS.tabOverview")}
          </TabsTrigger>
          <TabsTrigger value="funnel">{t("ANALYTICS.tabFunnel")}</TabsTrigger>
          <TabsTrigger value="funnel-guest">
            {t("ANALYTICS.tabFunnelGuest")}
          </TabsTrigger>
          <TabsTrigger value="app">{t("ANALYTICS.tabApp")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {cards.map((card) => (
              <StatsCard
                key={card.title}
                title={card.title}
                value={card.value}
                displayValue={
                  "displayValue" in card ? card.displayValue : undefined
                }
              />
            ))}
          </div>

          <TimeSeriesChartCard
            title={`${t("ANALYTICS.activeUsers")} · ${
              range === "live"
                ? t("ANALYTICS.rangeLive")
                : range === "1d"
                  ? t("ANALYTICS.rangeToday")
                  : range
            }`}
            points={chartPoints}
            palette={analyticsPalette}
          />

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold">
                  {t("ANALYTICS.topPages")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <TopPagesTable
                  points={topPagesQuery.data ?? []}
                  isLoading={topPagesQuery.isLoading}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold">
                  {t("ANALYTICS.topReferrers")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <TopReferrersTable
                  points={topReferrersQuery.data ?? []}
                  isLoading={topReferrersQuery.isLoading}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold">
                  {t("ANALYTICS.topCountries")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BreakdownTable
                  rows={countriesQuery.data ?? []}
                  isLoading={countriesQuery.isLoading}
                  labelHeader={t("ANALYTICS.country")}
                  valueHeader={t("ANALYTICS.visitors")}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold">
                  {t("ANALYTICS.topBrowsers")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BreakdownTable
                  rows={browsersQuery.data ?? []}
                  isLoading={browsersQuery.isLoading}
                  labelHeader={t("ANALYTICS.browser")}
                  valueHeader={t("ANALYTICS.visitors")}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold">
                  {t("ANALYTICS.topOs")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BreakdownTable
                  rows={osQuery.data ?? []}
                  isLoading={osQuery.isLoading}
                  labelHeader={t("ANALYTICS.os")}
                  valueHeader={t("ANALYTICS.visitors")}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold">
                  {t("ANALYTICS.topDevices")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BreakdownTable
                  rows={devicesQuery.data ?? []}
                  isLoading={devicesQuery.isLoading}
                  labelHeader={t("ANALYTICS.device")}
                  valueHeader={t("ANALYTICS.visitors")}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-semibold">
                  {t("ANALYTICS.topLanguages")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BreakdownTable
                  rows={languagesQuery.data ?? []}
                  isLoading={languagesQuery.isLoading}
                  labelHeader={t("ANALYTICS.language")}
                  valueHeader={t("ANALYTICS.visitors")}
                />
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="funnel">
          <FunnelView range={range} granularity={granularity} />
        </TabsContent>

        <TabsContent value="funnel-guest">
          <FunnelGuestView range={range} />
        </TabsContent>

        <TabsContent value="app">
          <AppView range={range} />
        </TabsContent>
      </Tabs>

      {isBusy ? <LoadingOverlay /> : null}
    </div>
  );
};

export default AnalyticsPage;
